import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Search,
  Loader2,
  X,
  History,
  Undo2,
  MailCheck,
  Mail,
  FileMinus,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'

import { apiClient, ApiError } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import DeductibleHistoryModal from '../components/DeductibleHistoryModal'
import { formatDateTime, formatEur, formatPeriod } from '../utils/deductibleFormat'

// ---------------------------------------------------------------------------
// Types (mirror app/models/deductible.py)
// ---------------------------------------------------------------------------

interface OrgOption {
  id: string
  name: string
  settings?: { fina?: { enabled?: boolean } }
}

interface InvoiceListItem {
  invoice_number: number
  period_year_month: string
  period_id: string
  kind: 'invoice' | 'credit_invoice' | 'replacement'
  customer_id: string | null
  line_count: number
  total: string
  replaces_invoice_number: number | null
  credits_invoice_number: number | null
  credited: boolean
  credit_status: 'pending' | 'credited' | null
  invoice_credit_id: string | null
  credit_invoice_number: number | null
  replacement_invoice_number: number | null
  credited_at: string | null
  monetra_notified_at: string | null
}

interface LineItem {
  id: string
  invoice_number: number | null
  invoice_line_number: number | null
  trip_started: string
  distance_meters: number | null
  address_from: string | null
  address_to: string | null
  customer_name_first: string | null
  customer_name_last: string | null
  copayment_value: string | number
  cost_center_description: string | null
  is_credit_note: boolean
  credited_invoice_number: number | null
  invoice_credit_id: string | null
  invoice_credit_role: 'credit' | 'replacement' | null
  bill_uuid: string | null
  booking_uuid: string | null
  pending_value: string | number | null
  pending_source: 'invoice_credit' | 'correction' | null
  pending_reason: string | null
}

function IdLine({ line }: { line: LineItem }) {
  const short = (u: string | null) => (u ? u.slice(0, 8) : '–')
  return (
    <div className="text-[11px] text-gray-400 font-mono" title={`bill ${line.bill_uuid ?? ''}
booking ${line.booking_uuid ?? ''}`}>
      bill {short(line.bill_uuid)} · booking {short(line.booking_uuid)}
    </div>
  )
}

interface PlanRow {
  line_id: string
  line_number: number | null
  trip_started: string | null
  value: string
  changed: boolean | null
  replayed_credit: boolean | null
  replayed_correction: boolean | null
  reason: string | null
}

interface PlanResponse {
  invoice_number: number
  period_year_month: string
  invoice_credit_id: string | null
  credit_rows: PlanRow[]
  replacement_rows: PlanRow[]
  skipped: PlanRow[]
  credit_total: string
  replacement_total: string
  net: string
}

type LineMode = 'keep' | 'value' | 'full'

const PAGE_SIZE = 100

function errorMessage(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (err instanceof ApiError) {
    const key = `deductibleInvoices.errors.${err.code}`
    const translated = t(key, (err.details ?? {}) as Record<string, unknown>)
    if (translated !== key) return translated
    const detailMsg = (err.details as { message?: string } | undefined)?.message
    return detailMsg || err.message
  }
  return err instanceof Error ? err.message : String(err)
}

// ---------------------------------------------------------------------------
// Credit dialog
// ---------------------------------------------------------------------------

function InvoiceCreditDialog({
  invoice,
  orgId,
  onClose,
  onDone,
}: {
  invoice: InvoiceListItem
  orgId: string
  onClose: () => void
  onDone: (msg: string) => void
}) {
  const { t } = useTranslation('billing')
  const [modes, setModes] = useState<Record<string, LineMode>>({})
  const [values, setValues] = useState<Record<string, string>>({})
  const [reason, setReason] = useState('')
  const [plan, setPlan] = useState<PlanResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: lines, isLoading } = useQuery<LineItem[]>({
    queryKey: ['deductibleInvoiceLines', orgId, invoice.invoice_number],
    queryFn: () =>
      apiClient.get(
        `/billing/deductibles/invoices/${invoice.invoice_number}/lines?organization_id=${orgId}`,
      ),
  })

  // Any change to the selections invalidates a previous preview
  const tripValues = useMemo(() => {
    const out: Record<string, string | null> = {}
    for (const ln of lines || []) {
      const mode = modes[ln.id] || 'keep'
      if (mode === 'full') out[ln.id] = null
      else if (mode === 'value') out[ln.id] = (values[ln.id] ?? '').replace(',', '.')
    }
    return out
  }, [lines, modes, values])
  useEffect(() => setPlan(null), [tripValues, reason])

  const invalidValue = Object.entries(tripValues).some(
    ([, v]) => v !== null && (v === '' || Number.isNaN(Number(v)) || Number(v) < 0),
  )

  const creditMutation = useMutation({
    mutationFn: (dryRun: boolean) =>
      apiClient.post<PlanResponse>(
        `/billing/deductibles/invoices/${invoice.invoice_number}/credit?organization_id=${orgId}`,
        { trip_values: tripValues, reason: reason || null, dry_run: dryRun },
      ),
    onSuccess: (data, dryRun) => {
      setError(null)
      if (dryRun) {
        setPlan(data)
      } else {
        onDone(
          t('deductibleInvoices.creditDone', {
            invoice: invoice.invoice_number,
            credit: formatEur(data.credit_total),
            replacement: formatEur(data.replacement_total),
          }),
        )
      }
    },
    onError: (err) => setError(errorMessage(err, t)),
  })

  const setMode = (id: string, mode: LineMode) => setModes((m) => ({ ...m, [id]: mode }))

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-5xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('deductibleInvoices.creditDialog.title', { invoice: invoice.invoice_number })}
            </h3>
            <p className="text-sm text-gray-500">
              {invoice.customer_id} · {formatPeriod(invoice.period_year_month)} ·{' '}
              {formatEur(invoice.total)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">{t('deductibleInvoices.creditDialog.help')}</p>

        {isLoading && <LoadingSpinner />}
        {lines && (
          <table className="min-w-full text-sm mb-4">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('deductibles.tripDate')}
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('deductibles.route')}
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('deductibles.copaymentValue')}
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('deductibleInvoices.creditDialog.newValue')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {lines.map((ln) => {
                const mode = modes[ln.id] || 'keep'
                return (
                  <tr key={ln.id} className={mode !== 'keep' ? 'bg-amber-50' : ''}>
                    <td className="px-2 py-1.5 text-gray-500">{ln.invoice_line_number}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{formatDateTime(ln.trip_started)}</td>
                    <td className="px-2 py-1.5 text-gray-700 max-w-xs">
                      <div className="truncate" title={`${ln.address_from} → ${ln.address_to}`}>
                        {ln.address_from} → {ln.address_to}
                        {ln.invoice_credit_role === 'replacement' && (
                          <span className="ml-1 text-xs text-gray-400">
                            ({t('deductibleInvoices.kind.replacement')})
                          </span>
                        )}
                      </div>
                      <IdLine line={ln} />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatEur(ln.copayment_value)}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-1">
                          <input type="radio" checked={mode === 'keep'} onChange={() => setMode(ln.id, 'keep')} />
                          {t('deductibleInvoices.creditDialog.keep')}
                        </label>
                        <label className="inline-flex items-center gap-1">
                          <input type="radio" checked={mode === 'value'} onChange={() => setMode(ln.id, 'value')} />
                          {t('deductibleInvoices.creditDialog.correct')}
                        </label>
                        {mode === 'value' && (
                          <input
                            type="text"
                            inputMode="decimal"
                            className="w-20 border rounded px-1.5 py-0.5 text-right"
                            placeholder="0,00"
                            value={values[ln.id] ?? ''}
                            onChange={(e) => setValues((v) => ({ ...v, [ln.id]: e.target.value }))}
                          />
                        )}
                        <label className="inline-flex items-center gap-1">
                          <input type="radio" checked={mode === 'full'} onChange={() => setMode(ln.id, 'full')} />
                          {t('deductibleInvoices.creditDialog.creditFully')}
                        </label>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('deductibleInvoices.creditDialog.reason')}
        </label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1.5 text-sm mb-4"
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('deductibleInvoices.creditDialog.reasonPlaceholder')}
        />

        {error && (
          <div className="mb-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {plan && plan.skipped.length > 0 && (
          <div className="mb-4 flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">
                {t('deductibleInvoices.creditDialog.skippedTitle', {
                  lines: plan.skipped.map((r) => `#${r.line_number ?? '?'}`).join(', '),
                  count: plan.skipped.length,
                })}
              </div>
              <div>{t('deductibleInvoices.creditDialog.skippedHelp')}</div>
            </div>
          </div>
        )}
        {plan && (
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded p-3">
              <div className="font-medium text-sm text-red-700 mb-1">
                {t('deductibleInvoices.creditDialog.previewCredit')} · {formatEur(plan.credit_total)}
              </div>
              <ul className="text-xs font-mono space-y-0.5">
                {plan.credit_rows.map((r) => (
                  <li key={r.line_id} className="flex justify-between">
                    <span>#{r.line_number} {formatDateTime(r.trip_started)}</span>
                    <span>{formatEur(r.value)}</span>
                  </li>
                ))}
              </ul>
            </div>
            {(() => {
              // A replacement whose total is negative is not issued as a
              // replacement: at approval its credit rows (replayed process B
              // credits of an earlier invoice) form a second credit invoice
              // and only positive rows (if any) form the replacement.
              const negative =
                Number(plan.replacement_total) <= 0 &&
                plan.replacement_rows.some((r) => Number(r.value) < 0)
              const creditPart = negative
                ? plan.replacement_rows.filter((r) => Number(r.value) < 0)
                : []
              const chargePart = negative
                ? plan.replacement_rows.filter((r) => Number(r.value) >= 0)
                : plan.replacement_rows
              const sum = (rows: PlanRow[]) => rows.reduce((a, r) => a + Number(r.value), 0)
              const rowList = (rows: PlanRow[]) => (
                <ul className="text-xs font-mono space-y-0.5">
                  {rows.map((r, i) => (
                    <li key={`${r.line_id}-${i}`} className="flex justify-between">
                      <span>
                        {formatDateTime(r.trip_started)}
                        {r.changed && <span className="ml-1 text-amber-700">*</span>}
                        {(r.replayed_credit || r.replayed_correction) && (
                          <span className="ml-1 text-gray-400">
                            ({t('deductibleInvoices.creditDialog.replayed')})
                          </span>
                        )}
                      </span>
                      <span>{formatEur(r.value)}</span>
                    </li>
                  ))}
                </ul>
              )
              return (
                <>
                  {negative && (
                    <div className="border rounded p-3 border-red-200">
                      <div className="font-medium text-sm text-red-700 mb-1">
                        {t('deductibleInvoices.creditDialog.previewSecondCredit')} · {formatEur(sum(creditPart))}
                      </div>
                      <div className="text-xs text-gray-500 mb-1">
                        {t('deductibleInvoices.creditDialog.secondCreditHelp')}
                      </div>
                      {rowList(creditPart)}
                    </div>
                  )}
                  <div className="border rounded p-3">
                    <div className="font-medium text-sm text-green-700 mb-1">
                      {t('deductibleInvoices.creditDialog.previewReplacement')} · {formatEur(sum(chargePart))}
                    </div>
                    {chargePart.length === 0 ? (
                      <div className="text-xs text-gray-500">
                        {t(
                          negative
                            ? 'deductibleInvoices.creditDialog.noReplacementNegative'
                            : 'deductibleInvoices.creditDialog.noReplacement',
                        )}
                      </div>
                    ) : (
                      rowList(chargePart)
                    )}
                  </div>
                </>
              )
            })()}
            <div className="md:col-span-2 text-sm text-gray-800">
              {t('deductibleInvoices.creditDialog.net', { net: formatEur(plan.net) })}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
            {t('deductibles.cancel')}
          </button>
          <button
            onClick={() => creditMutation.mutate(true)}
            disabled={!lines || invalidValue || creditMutation.isPending}
            className="inline-flex items-center px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50"
          >
            {creditMutation.isPending && creditMutation.variables === true && (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            )}
            {t('deductibleInvoices.creditDialog.preview')}
          </button>
          <button
            onClick={() => {
              const msg =
                t('deductibleInvoices.creditDialog.confirm', { invoice: invoice.invoice_number }) +
                (plan && plan.skipped.length > 0
                  ? '\n\n' +
                    t('deductibleInvoices.creditDialog.confirmSkipped', {
                      count: plan.skipped.length,
                      lines: plan.skipped.map((r) => `#${r.line_number ?? '?'}`).join(', '),
                    })
                  : '')
              if (confirm(msg)) creditMutation.mutate(false)
            }}
            disabled={!plan || invalidValue || creditMutation.isPending}
            className="inline-flex items-center px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            title={!plan ? t('deductibleInvoices.creditDialog.previewFirst') : undefined}
          >
            {creditMutation.isPending && creditMutation.variables === false && (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            )}
            <FileMinus className="w-4 h-4 mr-1" />
            {t('deductibleInvoices.creditDialog.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DeductibleInvoicesPage() {
  const { t } = useTranslation('billing')
  const queryClient = useQueryClient()

  const [orgId, setOrgId] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [creditTarget, setCreditTarget] = useState<InvoiceListItem | null>(null)
  const [historyLine, setHistoryLine] = useState<LineItem | null>(null)
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const showBanner = (type: 'success' | 'error', message: string) => {
    setBanner({ type, message })
    setTimeout(() => setBanner(null), type === 'success' ? 6000 : 12000)
  }

  const { data: orgsResp } = useQuery<{ data: OrgOption[] }>({
    queryKey: ['organizations'],
    queryFn: () => apiClient.get('/organizations'),
    staleTime: 5 * 60 * 1000,
  })
  const orgsWithFina = useMemo(
    () => (orgsResp?.data || []).filter((o) => o.settings?.fina?.enabled),
    [orgsResp],
  )
  useEffect(() => {
    if (!orgId && orgsWithFina.length > 0) setOrgId(orgsWithFina[0].id)
  }, [orgId, orgsWithFina])

  // Debounced search
  useEffect(() => {
    const h = setTimeout(() => {
      setSearch(searchInput.trim())
      setOffset(0)
    }, 350)
    return () => clearTimeout(h)
  }, [searchInput])

  const invoicesQuery = useQuery<{ items: InvoiceListItem[]; total: number }>({
    queryKey: ['deductibleInvoices', orgId, search, offset],
    enabled: !!orgId,
    queryFn: async () => {
      const params = new URLSearchParams({
        organization_id: orgId,
        limit: String(PAGE_SIZE),
        offset: String(offset),
      })
      if (search) params.set('search', search)
      const resp = await apiClient.getRaw(`/billing/deductibles/invoices?${params}`)
      const items = (await resp.json()) as InvoiceListItem[]
      return { items, total: Number(resp.headers.get('X-Total-Count') || items.length) }
    },
  })

  const linesQuery = useQuery<LineItem[]>({
    queryKey: ['deductibleInvoiceLines', orgId, expanded],
    enabled: !!orgId && expanded !== null,
    queryFn: () =>
      apiClient.get(`/billing/deductibles/invoices/${expanded}/lines?organization_id=${orgId}`),
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['deductibleInvoices'] })
    queryClient.invalidateQueries({ queryKey: ['deductibleInvoiceLines'] })
    queryClient.invalidateQueries({ queryKey: ['deductibleLineHistory'] })
  }

  const cancelMutation = useMutation({
    mutationFn: (invoiceCreditId: string) =>
      apiClient.delete(`/billing/deductibles/invoice-credits/${invoiceCreditId}`),
    onSuccess: () => {
      refresh()
      showBanner('success', t('deductibleInvoices.cancelDone'))
    },
    onError: (err) => showBanner('error', errorMessage(err, t)),
  })

  const monetraMutation = useMutation({
    mutationFn: ({ id, notified }: { id: string; notified: boolean }) =>
      apiClient.post(
        `/billing/deductibles/invoice-credits/${id}/monetra-notified?notified=${notified}`,
        {},
      ),
    onSuccess: () => refresh(),
    onError: (err) => showBanner('error', errorMessage(err, t)),
  })

  const items = invoicesQuery.data?.items || []
  const total = invoicesQuery.data?.total || 0

  const kindBadge = (inv: InvoiceListItem) => {
    if (inv.kind === 'credit_invoice')
      return (
        <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
          {t('deductibleInvoices.kind.credit_invoice')}
        </span>
      )
    if (inv.kind === 'replacement')
      return (
        <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
          {t('deductibleInvoices.kind.replacement')}
        </span>
      )
    return (
      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
        {t('deductibleInvoices.kind.invoice')}
      </span>
    )
  }

  const statusCell = (inv: InvoiceListItem) => {
    if (inv.kind === 'credit_invoice' && inv.credits_invoice_number)
      return (
        <span className="text-xs text-gray-600">
          {t('deductibleInvoices.lineCredits', { invoice: inv.credits_invoice_number })}
        </span>
      )
    if (inv.replaces_invoice_number)
      return (
        <span className="text-xs text-gray-600">
          {t('deductibleInvoices.replaces', { invoice: inv.replaces_invoice_number })}
        </span>
      )
    if (!inv.credited) return <span className="text-xs text-gray-400">–</span>
    if (inv.credit_status === 'pending')
      return (
        <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800">
          {t('deductibleInvoices.creditStatus.pending')}
        </span>
      )
    return (
      <span className="text-xs text-gray-700">
        {t('deductibleInvoices.creditedWith', {
          credit: inv.credit_invoice_number,
          replacement: inv.replacement_invoice_number ?? '–',
        })}
      </span>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/payment-traffic" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('deductibleInvoices.title')}</h1>
            <p className="text-sm text-gray-500">{t('deductibleInvoices.subtitle')}</p>
          </div>
        </div>
      </div>

      {banner && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            banner.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {banner.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {banner.message}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border">
        <div className="px-6 py-4 border-b flex flex-wrap gap-3 items-center">
          <select
            value={orgId}
            onChange={(e) => {
              setOrgId(e.target.value)
              setOffset(0)
              setExpanded(null)
            }}
            className="border rounded-lg px-3 py-1.5 text-sm"
          >
            {orgsWithFina.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('deductibleInvoices.searchPlaceholder')}
              className="w-full border rounded-lg pl-8 pr-3 py-1.5 text-sm"
            />
          </div>
          <div className="text-sm text-gray-500 ml-auto">
            {invoicesQuery.isFetching && <Loader2 className="w-4 h-4 inline animate-spin mr-1" />}
            {t('deductibleInvoices.count', { count: total })}
          </div>
        </div>

        {invoicesQuery.isLoading ? (
          <div className="p-8">
            <LoadingSpinner />
          </div>
        ) : invoicesQuery.error ? (
          <div className="px-6 py-8 text-sm text-red-600">{errorMessage(invoicesQuery.error, t)}</div>
        ) : items.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">{t('deductibleInvoices.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8" />
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('deductibles.invoiceNumber')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('deductibles.period')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('deductibleInvoices.kindHeader')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('deductibleInvoices.customerId')}
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('deductibles.lineCount')}
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('deductibles.totalAmount')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('deductibleInvoices.statusHeader')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('deductibleInvoices.monetraHeader')}
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('deductibles.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((inv) => {
                  const isOpen = expanded === inv.invoice_number
                  const canCredit = inv.kind !== 'credit_invoice' && !inv.credited
                  return (
                    <Fragment key={inv.invoice_number}>
                      <tr className="hover:bg-gray-50">
                        <td className="pl-3">
                          <button
                            onClick={() => setExpanded(isOpen ? null : inv.invoice_number)}
                            className="text-gray-400 hover:text-gray-700"
                            title={t('deductibleInvoices.showLines')}
                          >
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 font-mono">{inv.invoice_number}</td>
                        <td className="px-3 py-2">{formatPeriod(inv.period_year_month)}</td>
                        <td className="px-3 py-2">{kindBadge(inv)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{inv.customer_id}</td>
                        <td className="px-3 py-2 text-right">{inv.line_count}</td>
                        <td className={`px-3 py-2 text-right font-mono ${Number(inv.total) < 0 ? 'text-red-700' : ''}`}>
                          {formatEur(inv.total)}
                        </td>
                        <td className="px-3 py-2">{statusCell(inv)}</td>
                        <td className="px-3 py-2">
                          {inv.credited && inv.invoice_credit_id && (
                            <button
                              onClick={() =>
                                monetraMutation.mutate({
                                  id: inv.invoice_credit_id!,
                                  notified: !inv.monetra_notified_at,
                                })
                              }
                              disabled={monetraMutation.isPending}
                              className={`inline-flex items-center px-2 py-1 text-xs rounded ${
                                inv.monetra_notified_at
                                  ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                              }`}
                              title={
                                inv.monetra_notified_at
                                  ? t('deductibleInvoices.monetraNotifiedAt', {
                                      at: formatDateTime(inv.monetra_notified_at),
                                    })
                                  : t('deductibleInvoices.markMonetraNotified')
                              }
                            >
                              {inv.monetra_notified_at ? (
                                <MailCheck className="w-3.5 h-3.5 mr-1" />
                              ) : (
                                <Mail className="w-3.5 h-3.5 mr-1" />
                              )}
                              {inv.monetra_notified_at
                                ? t('deductibleInvoices.monetraNotified')
                                : t('deductibleInvoices.monetraNotNotified')}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {canCredit && (
                            <button
                              onClick={() => setCreditTarget(inv)}
                              className="inline-flex items-center px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                            >
                              <FileMinus className="w-3.5 h-3.5 mr-1" />
                              {t('deductibleInvoices.creditInvoice')}
                            </button>
                          )}
                          {inv.credited && inv.credit_status === 'pending' && inv.invoice_credit_id && (
                            <button
                              onClick={() => {
                                if (confirm(t('deductibleInvoices.confirmCancel', { invoice: inv.invoice_number })))
                                  cancelMutation.mutate(inv.invoice_credit_id!)
                              }}
                              disabled={cancelMutation.isPending}
                              className="inline-flex items-center px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 disabled:opacity-50"
                            >
                              <Undo2 className="w-3.5 h-3.5 mr-1" />
                              {t('deductibleInvoices.cancelCredit')}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-50">
                          <td colSpan={10} className="px-6 py-3">
                            {linesQuery.isLoading ? (
                              <LoadingSpinner />
                            ) : (
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500">
                                    <th className="px-2 py-1 text-left">#</th>
                                    <th className="px-2 py-1 text-left">{t('deductibles.tripDate')}</th>
                                    <th className="px-2 py-1 text-left">{t('deductibles.route')}</th>
                                    <th className="px-2 py-1 text-left">{t('deductibles.costCenterDesc')}</th>
                                    <th className="px-2 py-1 text-right">{t('deductibles.copaymentValue')}</th>
                                    <th className="px-2 py-1 text-left">{t('deductibleInvoices.lineStatus')}</th>
                                    <th className="px-2 py-1" />
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {(linesQuery.data || []).map((ln) => (
                                    <tr key={ln.id}>
                                      <td className="px-2 py-1 text-gray-500">{ln.invoice_line_number}</td>
                                      <td className="px-2 py-1 whitespace-nowrap">{formatDateTime(ln.trip_started)}</td>
                                      <td className="px-2 py-1 max-w-md">
                                        <div className="truncate" title={`${ln.address_from} → ${ln.address_to}`}>
                                          {ln.address_from} → {ln.address_to}
                                        </div>
                                        <IdLine line={ln} />
                                      </td>
                                      <td className="px-2 py-1 text-gray-600">{ln.cost_center_description}</td>
                                      <td className={`px-2 py-1 text-right font-mono ${Number(ln.copayment_value) < 0 ? 'text-red-700' : ''}`}>
                                        {formatEur(ln.copayment_value)}
                                      </td>
                                      <td className="px-2 py-1 text-gray-600">
                                        {ln.pending_source &&
                                          (Number(ln.pending_value) === Number(ln.copayment_value) ? (
                                            <div className="text-gray-400" title={ln.pending_reason ?? undefined}>
                                              {t('deductibleInvoices.pendingUnchanged')}
                                            </div>
                                          ) : (
                                            <div
                                              className="text-amber-700 font-medium"
                                              title={ln.pending_reason ?? undefined}
                                            >
                                              {Number(ln.pending_value) === 0
                                                ? t('deductibleInvoices.pendingFullCredit')
                                                : t('deductibleInvoices.pendingValue', {
                                                    value: formatEur(ln.pending_value ?? 0),
                                                  })}
                                              <span className="ml-1 font-normal text-amber-600">
                                                ({t(`deductibleInvoices.pendingSource.${ln.pending_source}`)})
                                              </span>
                                            </div>
                                          ))}
                                        {ln.invoice_credit_role === 'credit' && ln.credited_invoice_number
                                          ? t('deductibleInvoices.lineCredits', { invoice: ln.credited_invoice_number })
                                          : ln.invoice_credit_role === 'replacement'
                                            ? t('deductibleInvoices.kind.replacement')
                                            : ln.is_credit_note && ln.credited_invoice_number
                                              ? t('deductibleInvoices.lineCredits', { invoice: ln.credited_invoice_number })
                                              : ''}
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        <button
                                          onClick={() => setHistoryLine(ln)}
                                          className="inline-flex items-center px-2 py-0.5 text-xs bg-white border rounded hover:bg-gray-100"
                                          title={t('deductibleInvoices.history.title')}
                                        >
                                          <History className="w-3.5 h-3.5 mr-1" />
                                          {t('deductibleInvoices.history.button')}
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="px-6 py-3 border-t flex items-center justify-between text-sm text-gray-600">
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} / {total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >
                ‹
              </button>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {creditTarget && orgId && (
        <InvoiceCreditDialog
          invoice={creditTarget}
          orgId={orgId}
          onClose={() => setCreditTarget(null)}
          onDone={(msg) => {
            setCreditTarget(null)
            refresh()
            showBanner('success', msg)
          }}
        />
      )}
      {historyLine && (
        <DeductibleHistoryModal
          source={{ lineId: historyLine.id }}
          subtitle={`${formatDateTime(historyLine.trip_started)} · ${historyLine.address_from} → ${historyLine.address_to}`}
          onClose={() => setHistoryLine(null)}
        />
      )}
    </div>
  )
}
